import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
} from 'react';
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';
import {
  Types,
  AuthState,
  JWTActions,
  SetUserType,
  AuthContextType,
  ProfileResponseType,
} from '../@types/auth';

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  isSubscribed: false,
  user: null,
};

const AuthReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case Types.Initial:
      return {
        isInitialized: true,
        user: action.payload.user,
        isSubscribed: action.payload.isSubscribed,
        isAuthenticated: action.payload.isAuthenticated,
      };
    case Types.Login:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isSubscribed: action.payload.isSubscribed,
      };
    case Types.Logout:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };

    case Types.Register:
      return {
        ...state,
        isAuthenticated: true,
        isSubscribed: action.payload.isSubscribed,
        user: action.payload.user,
      };

    case Types.SetUser:
      return {
        ...state,
        ...(state.user
          ? {
              user: { ...state.user, ...action.payload },
              ...(action.payload.is_subscribed
                ? { isSubscribed: action.payload.is_subscribed }
                : {}),
            }
          : {}),
      };

    default:
      return state;
  }
};

const fetchProfile = async (): Promise<any> => {
  const { data: user } = await axios
    .get<ProfileResponseType>(`/api/v1/auth/profile?t=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    })
    .then((res) => res.data);
  return user;
};

const AuthContext = createContext<AuthContextType | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(AuthReducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = window.localStorage.getItem('accessToken');

      if (accessToken) {
        await setSession(accessToken);

        const user = await fetchProfile();

        dispatch({
          type: Types.Initial,
          payload: {
            user,
            isAuthenticated: true,
            isSubscribed: user.is_subscribed,
          },
        });
      } else {
        dispatch({
          type: Types.Initial,
          payload: {
            user: null,
            isSubscribed: false,
            isAuthenticated: false,
          },
        });
      }
    } catch (err) {
      dispatch({
        type: Types.Initial,
        payload: {
          user: null,
          isSubscribed: false,
          isAuthenticated: false,
        },
      });
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await axios.post('/api/v1/auth/login', {
      email,
      password,
    });

    const accessToken = response.data;
    await setSession(accessToken);

    const user = await fetchProfile();

    dispatch({
      type: Types.Login,
      payload: {
        user,
        isSubscribed: user.is_subscribed,
      },
    });
  };

  const register = async (params: {
    email: string;
    password: string;
    name: string;
  }) => {
    const response = await axios.post('/api/v1/auth/register', params);
    const accessToken = response.data;
    await setSession(accessToken);

    const user = await fetchProfile();

    dispatch({
      type: Types.Register,
      payload: {
        isSubscribed: user.is_subscribed,
        user,
      },
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: Types.Logout });
    await axios.post('/api/v1/auth/logout');
  };

  const forgotPassword = async (email: string) => {
    await axios.post('/api/v1/auth/forgot-password', {
      email,
    });
  };

  const resetPassword = async (params: {token: string, password: string, email: string}) => {
    await axios.post('/api/v1/auth/reset-password', params);
  };

  const setUser = (user: SetUserType) => {
    dispatch({ type: Types.SetUser, payload: user });
  };

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (!state.isInitialized) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        setUser,
        register,
        resetPassword,
        forgotPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };